import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { login as apiLogin } from "../lib/api";
import PulseLogo from "../components/PulseLogo";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await apiLogin(username, password);
      localStorage.setItem("pulse_token", data.token);
      localStorage.setItem("pulse_user", JSON.stringify(data.user));
      navigate("/");
    } catch (err) {
      const msg = err?.response?.data?.detail || "שגיאת התחברות. נסה שוב.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-obsidian grain relative overflow-hidden" data-testid="login-page">
      {/* Animated background glows */}
      <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-primary/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-severity-anomaly/5 rounded-full blur-[120px]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md px-8"
      >
        {/* Logo + brand */}
        <div className="flex items-center gap-3 mb-12 justify-center">
          <PulseLogo size={44} />
          <div>
            <h1 className="text-2xl font-bold tracking-tight leading-none" style={{ letterSpacing: "-0.02em" }}>
              Opty<span className="text-primary">go</span>
              <span className="text-zinc-400 font-medium ms-2">Pulse</span>
            </h1>
            <p className="text-[11px] text-zinc-500 mt-1.5 tracking-[0.18em] uppercase font-mono">Smart Business Insights</p>
          </div>
        </div>

        {/* Tagline */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-10 text-center"
        >
          <p className="text-zinc-400 text-sm leading-relaxed">
            שכבת אינטליגנציה מעל ה-ERP שלך.
            <br />
            <span className="text-zinc-500">פעם אחת אנושית.</span>
          </p>
        </motion.div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="block text-xs text-zinc-500 mb-2 font-medium">שם משתמש</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 bg-card border border-border rounded-md text-foreground placeholder-zinc-600 focus:border-primary focus:outline-none transition-colors"
              placeholder="שם משתמש"
              data-testid="login-username"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-2 font-medium">סיסמה</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-card border border-border rounded-md text-foreground placeholder-zinc-600 focus:border-primary focus:outline-none transition-colors"
              placeholder="••••••••"
              data-testid="login-password"
              required
            />
          </div>

          {error && (
            <div className="text-sm text-severity-critical px-2" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-6 py-3.5 bg-primary text-primary-foreground rounded-md font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-60 transition-all hover:shadow-[0_0_24px_rgba(0,229,255,0.35)]"
            data-testid="login-submit-btn"
          >
            {loading ? (
              <span className="text-sm">מתחבר...</span>
            ) : (
              <span className="text-sm">היכנס ל-Optygo Pulse</span>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
